// Card / Level: one of the night's levels as a full-width card (Aaron,
// 2026-11-02, replacing the 2026-10-19 row). The whole card is the button:
// the kind's icon as tall as the name and one line of tagline, the name with
// the star count after it, and the tagline under them. A level's state is a
// small round mark after the count, the glyph inside a ring of the count's
// outline and height: play while in progress, a check when done, a lock when
// locked; a new level has none and its count sits at the edge. A done card recedes onto a darker panel with
// its words dimmed, so the unfinished ones stand out. A week's level is
// titled XL (Aaron, 2026-11-01).
import { levelTitle, type Variant } from '@wits/core';
import type { TodayLevel } from '../../systems/api/types';
import { Icon, type IconName } from '../components/Icon';

const ICONS: Record<Variant, IconName> = {
  phrase: 'phrase',
  quotation: 'quotation',
  category: 'category',
  apogee: 'apogee',
  assemblage: 'assemblage',
};

const TAGLINES: Record<Variant, string> = {
  phrase: 'Find the unique solution.',
  quotation: 'Find the unique solution.',
  category: 'Find the unique solution.',
  apogee: 'Play for the high score.',
  assemblage: 'Use every star.',
};

type Mark = 'mark-play' | 'mark-check' | 'mark-lock';

export function LevelCard({ level, onPlay }: { level: TodayLevel; onPlay: () => void }) {
  const name = levelTitle(level.variant, level.layer);
  const done = level.status === 'solved' || level.status === 'finished';
  const mark: Mark | null = level.locked
    ? 'mark-lock'
    : done
      ? 'mark-check'
      : level.status === 'in-progress'
        ? 'mark-play'
        : null;
  const state = level.locked
    ? 'Locked'
    : level.status === 'solved'
      ? 'Solved'
      : level.status === 'finished'
        ? 'Finished'
        : level.status === 'in-progress'
          ? 'In progress'
          : null;
  const stars = `${level.stars} ${level.stars === 1 ? 'star' : 'stars'}`;
  const label = [name, stars, state?.toLowerCase()].filter(Boolean).join(', ') + `. ${TAGLINES[level.variant]}`;
  return (
    <button
      type="button"
      className={['panel', 'level-card', done ? 'is-done' : ''].join(' ').trim()}
      data-mode={level.variant}
      data-layer={level.layer}
      onClick={onPlay}
      aria-label={label}
    >
      <Icon name={ICONS[level.variant]} className="level-card-icon" />
      <span className="level-card-body">
        <span className="level-card-head">
          <span className="level-card-title">{name}</span>
          <span className="level-card-stars mono">
            <Icon name="star-4" className="level-card-star" />
            {level.stars}
          </span>
          {mark && (
            <span className="level-card-mark">
              <Icon name={mark} className="level-card-mark-glyph" />
            </span>
          )}
        </span>
        <span className="level-card-tagline">{TAGLINES[level.variant]}</span>
      </span>
    </button>
  );
}
